<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte';
  import type { Snippet } from 'svelte';
  import { trapFocus } from '$lib/utils/focusTrap';

  type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

  let {
    open = $bindable(false),
    size = 'md',
    bare = false,
    backdropClass = '',
    onclose = () => {},
    children,
    header,
    footer
  }: {
    open?: boolean;
    size?: ModalSize;
    /**
     * Render only the structural panel (no built-in chrome) so the caller can
     * lay out its own header/body/footer, e.g. full-bleed "atmospheric" dialogs.
     */
    bare?: boolean;
    /** Replaces the default `bg-black/50` overlay wash (animation classes stay). */
    backdropClass?: string;
    onclose?: () => void;
    children?: Snippet;
    header?: Snippet;
    footer?: Snippet;
  } = $props();

  let modalElement: HTMLDivElement | undefined;
  let previouslyFocusedElement: HTMLElement | null = null;
  let cleanup: (() => void) | undefined;
  let escapeListenerAdded = false;

  const sizeClasses: Record<ModalSize, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl'
  };

  const panelClass = $derived(
    bare
      ? `mx-4 w-full overflow-hidden pop-in ${sizeClasses[size]}`
      : `mx-4 w-full max-h-[90vh] overflow-hidden rounded-toyama bg-bg-canvas shadow-toyama-xl pop-in ${sizeClasses[size]}`
  );

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      onclose();
    }
  }

  function handleEscape(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      onclose();
    }
  }

  function setupModal() {
    if (!escapeListenerAdded) {
      document.addEventListener('keydown', handleEscape);
      escapeListenerAdded = true;
    }
    previouslyFocusedElement = document.activeElement as HTMLElement | null;
    tick().then(() => {
      modalElement?.focus();
      if (modalElement && !cleanup) {
        cleanup = trapFocus(modalElement);
      }
    });
  }

  function teardownModal() {
    if (escapeListenerAdded) {
      document.removeEventListener('keydown', handleEscape);
      escapeListenerAdded = false;
    }
    if (cleanup) {
      cleanup();
      cleanup = undefined;
    }
  }

  onMount(() => {
    if (open) {
      setupModal();
    }
  });

  onDestroy(() => {
    teardownModal();
    if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
      previouslyFocusedElement.focus();
    }
  });

  $effect(() => {
    if (open) {
      setupModal();
    } else {
      teardownModal();
    }
  });
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y-no-noninteractive-element-interactions -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center backdrop-in {backdropClass ||
      'bg-black/50'}"
    onclick={handleBackdropClick}
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
    bind:this={modalElement}
    tabindex="-1"
  >
    <div class={panelClass}>
      {#if bare}
        {#if children}
          {@render children()}
        {/if}
      {:else}
        {#if header}
          <div class="px-6 py-4 border-b border-toyama-cloudy-gray" id="modal-title">
            {@render header()}
          </div>
        {/if}

        <div class="px-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {#if children}
            {@render children()}
          {/if}
        </div>

        {#if footer}
          <div class="px-6 py-4 border-t border-toyama-cloudy-gray bg-toyama-light-carbon">
            {@render footer()}
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}
